import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../../core";
import { EditExerciseRequest, Exercise } from "../../../types/workout";

interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  result?: any;
  results?: T;
}

export const exerciseApi = createApi({
  reducerPath: "exerciseApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Exercise", "Exercises"],

  endpoints: (builder) => ({
    getExercises: builder.query<
      any,
      { muscle?: string; equipment?: string; name?: string; type?: string; page?: number; limit?: number } | void
    >({
      query: (params) => {
        let url = `${ApiEndpoints.getAllWorkouts}`;
        const query: string[] = [];
        if (params?.muscle)
          query.push(`muscle=${encodeURIComponent(params.muscle)}`);
        if (params?.equipment)
          query.push(`equipment=${encodeURIComponent(params.equipment)}`);
        if (params?.name)
          query.push(`name=${encodeURIComponent(params.name)}`);
        if (params?.type)
          query.push(`type=${encodeURIComponent(params.type)}`);
        if (params?.page)
          query.push(`page=${params.page}`);
        if (params?.limit)
          query.push(`limit=${params.limit}`);
        if (query.length) url += `?${query.join("&")}`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Exercises"],
    }),

    searchExercises: builder.query<any, string>({
      query: (name) => ({
        url: `${ApiEndpoints.getAllWorkouts}?name=${encodeURIComponent(name)}`,
        method: "GET",
      }),
      providesTags: ["Exercises"],
    }),

    getExerciseById: builder.query<any, string>({
      query: (exerciseid) => ({
        url: `${ApiEndpoints.getWorkoutById}/${exerciseid}`,
        method: "GET",
      }),
      providesTags: (_result, _error, exerciseid) => [
        { type: "Exercise", id: exerciseid },
      ],
    }),

    createExercise: builder.mutation<
      ApiResponse<any>,
      {
        name: string;
        muscles: string[];
        equipment: string;
        thumbnail?: string | null;
        video: string | null;
      }
    >({
      query: (data) => {
        const body: any = {
          name: data?.name,
          muscles: data?.muscles,
          equipment: data?.equipment,
          thumbnail: data?.thumbnail,
          video: data?.video,
        };
        if (data?.thumbnail !== undefined) body.thumbnail = data.thumbnail;
        if (data?.video !== undefined) body.video = data.video;

        return {
          url: `${ApiEndpoints.createWorkout}`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Exercises"],
    }),

    // Edit exercise (was editExercise)
    editExercise: builder.mutation<
      ApiResponse<Exercise>,
      { id: string } & Partial<EditExerciseRequest>
    >({
      query: ({ id, ...data }) => {
        return {
          url: `${ApiEndpoints.editWorkout}/${id}`,
          method: "PATCH",
          body: data,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Exercise", id },
        "Exercises",
      ],
    }),

    // Delete exercise (was deleteGym)
    deleteExercise: builder.mutation<ApiResponse<{ ok: boolean }>, string>({
      query: (exerciseid) => ({
        url: `${ApiEndpoints.deleteWorkout}/${exerciseid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Exercises"],
    }),
  }),
});

export const {
  useGetExercisesQuery,
  useSearchExercisesQuery,
  useGetExerciseByIdQuery,
  useCreateExerciseMutation,
  useEditExerciseMutation,
  useDeleteExerciseMutation,
} = exerciseApi;
